import { useModals } from "@/features/modals/hooks";
import { type WidgetSettingsDefinition, useProfile, useTeam } from "@repo/api";
import { t } from "@repo/translation";
import { CoworkerAndTeamSelectorListItem } from "@repo/ui";
import { isDefined } from "@repo/utils";
import isEmpty from "lodash/isEmpty";
import { useEffect, useState } from "react";

export const getCoworkerSettingsValue = (settingsValue: string) => {
  try {
    const parsedValue =
      settingsValue && settingsValue !== "" ? JSON.parse(settingsValue) : "";

    const selectedTeam = parsedValue?.selectedTeam;
    const selectedCoWorkers = parsedValue?.selectedCoWorkers;
    const assignedToTeam = parsedValue?.assignedToTeam;
    const fromDate = parsedValue?.fromDate;
    const toDate = parsedValue?.toDate;

    return {
      email: selectedCoWorkers,
      teamId: selectedTeam as number,
      assignedToTeam: assignedToTeam as boolean,
      fromDate,
      toDate,
    };
  } catch {
    return {
      email: undefined,
      teamId: undefined,
      assignedToTeam: false,
      fromDate: undefined,
      toDate: undefined,
    };
  }
};

const setCoworkerSettingsValue = (
  selectedTeam?: number,
  selectedCoWorkers?: string[],
  assignedToTeam?: boolean
) => {
  return JSON.stringify({
    selectedTeam,
    selectedCoWorkers,
    assignedToTeam: assignedToTeam ?? false,
  });
};

export const CoworkerSettingsControl = ({
  settingDefinition,
  setting,
  setSetting,
}: {
  settingDefinition: WidgetSettingsDefinition;
  setting: Record<string, any>;
  setSetting: (newSetting: Record<string, object>) => void;
}) => {
  const { pushModal } = useModals();
  const { data: profile } = useProfile();
  const { email, teamId, assignedToTeam } = getCoworkerSettingsValue(
    setting[settingDefinition.id] ?? ""
  );

  const [localSettings, setLocalSettings] = useState<string | undefined>(
    undefined
  );
  const { data: teamData } = useTeam(teamId);
  const teamSelection = teamData
    ? {
        kind: "team" as const,
        team: teamData,
        assignedToTeam: assignedToTeam ?? false,
        members: (teamData.teamMembers ?? []).map((m) => ({
          ...m,
          selected: true,
        })),
      }
    : undefined;

  // Set default coworker if specified in the control
  useEffect(() => {
    if (
      isEmpty(email) &&
      teamId === undefined &&
      settingDefinition.additionalControl?.defaultCoworker === true
    ) {
      setSetting({
        ...setting,
        [settingDefinition.id]: setCoworkerSettingsValue(
          undefined,
          profile?.mail ? [profile.mail] : [],
          false
        ),
      });
    }
  }, [email, teamId, settingDefinition, profile, setting, setSetting]);

  // I need to have this as an effect since set team and assignedToTeam
  // is two different callbacks
  useEffect(() => {
    if (localSettings !== undefined) {
      setSetting({
        ...setting,
        [settingDefinition.id]: localSettings,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSettings]);

  const required = settingDefinition.required;

  return (
    <CoworkerAndTeamSelectorListItem
      selectionMode={{
        coworkerSelectionMode: "MULTI",
        selectTeamWithMembers: true,
        selectOnlyTeam: true,
        toggleTeamMembers: true,
      }}
      pushModal={pushModal}
      selected={teamSelection ?? email?.filter(isDefined)}
      title={t("label.coworker_and_team")}
      searchContext="CALENDAR"
      setSelected={(s) => {
        if (s && !Array.isArray(s)) {
          setLocalSettings((prev: any) => {
            const { email: newEmail } = getCoworkerSettingsValue(prev);
            return setCoworkerSettingsValue(
              s.team.teamId,
              newEmail,
              s.assignedToTeam
            );
          });
        } else {
          setLocalSettings((prev: any) => {
            const { teamId: newTeamId, assignedToTeam: newAssignedToTeam } =
              getCoworkerSettingsValue(prev);
            return setCoworkerSettingsValue(
              newTeamId,
              s?.map((coworker) => coworker.emailId),
              newAssignedToTeam
            );
          });
        }
      }}
      required={required}
    />
  );
};
